import styles from "./CompanyDetails.module.scss";
import { UnderwritingFormContext } from "../context/UnderwritingFormContext";
import {
  createRef,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Field, Form, FormSpy, useField, useForm } from "react-final-form";
import arrayMutators from "final-form-arrays";
import OutlinedFormInput from "../../Finance/components/OutlinedFormInput/OutlinedFormInput";
import MeritoSelectField from "../components/selectfields/MeritoSelectField";
import {
  cinNumberValidation,
  companyWebsiteValidation,
  gstNumberValidation,
  panNumbervalidation,
} from "../../../components/functions/formValidation";
import {
  ApplicationContextType,
  UnderwritingSteps,
  alignmentType,
  validationErrorTypes,
} from "../../../interfaces/interfaces";
import {
  findFirstErrorField,
  setFieldTouchedMutator,
} from "../../../components/functions/utilities";
import MeritoButton from "../../../components/common/MeritoButtons/MeritoButtons";
import { HiArrowNarrowDown as DownArrowIcon } from "react-icons/hi";
import { unstable_usePrompt, useSearchParams } from "react-router-dom";
import usePrompt from "../../../components/Hooks/useBlocker";
import API from "../../../rest/api";
import Shareholders from "./Shareholders";
import MeritoRadioButton from "../../Finance/components/RadioButton/MeritoRadioButton";
import {
  TableForm,
  TableFormBody,
  TableFormRow,
} from "../components/TableForm/TableForm";
import TableUpload, {
  validateFile,
} from "../components/TableForm/TableUploadCell";
import { OnChange } from "react-final-form-listeners";
import dayjs from "dayjs";
import MeritoDateField from "../../Finance/components/DateField/MeritoDateField";
import CompanyDetailsUpload from "./CompanyDetailsUpload";
import ShareholdersRevamp from "./ShareholdersRevamp";
import { Modal } from "reactstrap";
import TableUploadRow from "../components/TableForm/TableUploadRow";
import ShareholderDetails from "./Shareholder/ShareholderDetails";
import { getFirstShareholdersGridInvalidRange } from "./Shareholder/shareholderHelper";
import { FieldArray } from "react-final-form-arrays";
import {
  companyDocsKeys,
  companyRows,
  validateCompanyDocs,
  validateCompanyFields,
  validateGstDetails,
} from "./companyDetailsHelper";
import { getIn } from "final-form";
import GstDetails from "./GstDetails/GstDetails";

type Props = {
  setFormPristine: (value: boolean) => void;
  saveToLocalStorage?: (values: Record<string, any>) => void;
};

const companyDocColumns = [
  {
    title: "Type",
    key: "type",
    alignment: alignmentType.leftAlign,
    width: "70%",
  },
  {
    title: "Attachment",
    key: "attachment",
    alignment: alignmentType.centerAlign,
    width: "30%",
  },
];

const CompanyDetails = forwardRef(({ setFormPristine, saveToLocalStorage }: Props, ref) => {
  const {
    formData,
    setFormData,
    brandCategoriesObj,
    formRefs,
    setStepValidity,
    formStepsValidity,
    formCompleteStatus,
    changeUploadStatus,
    underwritingFileJobs,
    gstAnalysisJobs,
  } = useContext(UnderwritingFormContext);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const sectionRootContainerRef = useRef(null);
  const [deleteFileKeys, setDeleteFileKeys] = useState([]);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [gstApiErrors, setGstApiErrors] = useState({});
  // const [formPristine, setFormPristine] = useState(true)

  const sectionRefs = useRef({
    companyDetails: createRef(),
    gstDetails: createRef(),
    shareholderDetails: createRef(),
  });

  const formRef = useRef(null);

  const [showUtilityRow, setShowUtilityRow] = useState(false);

  const [currentVisibleSectionIndex, setCurrentVisibleSectionIndex] =
    useState(0);
  const [isShareholderModal, setIsShareholderModal] = useState(false);

  const [newUploadedDocIds, setNewUploadedDocIds] = useState<number[]>([]);
  const [deleteDocIds, setDeleteDocIds] = useState<number[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const showError = searchParams.get("error");
    if (showError == "true") {
      setTimeout(() => {
        formRef?.current?.submit();
        scrollToFirstError();
      }, 0);
    }
  }, [searchParams]);

  // Scroll to the next section function, using the currentVisibleSectionIndex
  const scrollToNextSection = useCallback(() => {
    const sectionKeys = Object.keys(sectionRefs.current);
    // if (sectionKeys.includes('taxes')) {
    //     let index = sectionKeys.indexOf('taxes');

    //     if (index !== -1) {
    //         // Remove the element from its current position
    //         let [element] = sectionKeys.splice(index, 1);

    //         // Add the element to the start of the array
    //         sectionKeys.unshift(element);
    //     }
    // }
    const nextSectionIndex = currentVisibleSectionIndex + 1; // Calculate the next section index
    const nextSectionKey = sectionKeys[nextSectionIndex]; // Get the key for the next section
    if (nextSectionKey) {
      const nextSection = sectionRefs.current[nextSectionKey].current;
      setCurrentVisibleSectionIndex(nextSectionIndex);
      nextSection?.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentVisibleSectionIndex]);

  // Determine the currently visible section based on the top 20% criteria
  const updateVisibleSectionIndex = useCallback(() => {
    const container = sectionRootContainerRef.current;
    const visibleThreshold = container.clientHeight * 0.2; // Top 20% height of the container
    let foundSectionIndex = null;

    const sectionKeys = Object.keys(sectionRefs.current);
    sectionKeys.forEach((key, index) => {
      const section = sectionRefs.current[key].current;
      // console.log('section', section, 'container', container);
      if (section && container) {
        const sectionTop = section.offsetTop - container.scrollTop;
        const sectionBottom = sectionTop + section.offsetHeight;
        // console.log('sectionTop', sectionTop, 'sectionBottom', sectionBottom, 'visibleThreshold', visibleThreshold)
        // If any part of the section is within the top 20% of the container
        if (sectionTop < visibleThreshold && sectionBottom > 0) {
          foundSectionIndex = index;
        }
      }
    });

    // console.log('foundSectionIndex', foundSectionIndex, 'currentVisibleSectionIndex', currentVisibleSectionIndex)

    // Update the state only if a different section is found
    if (
      foundSectionIndex !== null &&
      foundSectionIndex !== currentVisibleSectionIndex
    ) {
      setCurrentVisibleSectionIndex(foundSectionIndex);
    } else {
      if (foundSectionIndex == null) {
        setCurrentVisibleSectionIndex(0);
      }
    }
  }, [currentVisibleSectionIndex]);

  // Listen to scroll events on the container and update the visible section index
  useEffect(() => {
    const container = sectionRootContainerRef.current;
    container.addEventListener("scroll", updateVisibleSectionIndex, {
      passive: true,
    });

    // Initial check in case the initial scroll position is not at the top
    updateVisibleSectionIndex();

    return () => {
      container.removeEventListener("scroll", updateVisibleSectionIndex);
    };
  }, [updateVisibleSectionIndex]);

  const checkScrollButtonVisibility = useCallback(() => {
    const container = sectionRootContainerRef.current;

    const sectionKeys = Object.keys(sectionRefs.current);
    const lastSectionKey = sectionKeys[sectionKeys.length - 1];
    const lastSectionRef = sectionRefs.current[lastSectionKey];
    if (container && lastSectionRef && lastSectionRef.current) {
      const { bottom } = lastSectionRef.current.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // console.log('bottom', bottom, 'containerRect', containerRect.bottom)
      // Check if the bottom of the last section is less than or equal to the container's bottom
      const threshold = 10;

      const isLastSectionFullyVisible =
        bottom <= containerRect.bottom + threshold;
      // console.log('isLastSectionFullyVisible', isLastSectionFullyVisible)
      // Hide the scroll button if the last section is fully visible
      setShowScrollButton(!isLastSectionFullyVisible);
    }
  }, []);

  useEffect(() => {
    const container = sectionRootContainerRef.current;

    const handleScroll = () => {
      checkScrollButtonVisibility();
    };

    container.addEventListener("scroll", handleScroll);

    // Initial check
    checkScrollButtonVisibility();

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [checkScrollButtonVisibility]);

  useEffect(() => {
    if (sectionRootContainerRef.current) {
      // Scrolling to the top of the container on component mount
      sectionRootContainerRef.current.scrollTop = 0;
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      submitForm: () => {
        return new Promise((resolve) => {
          formRef.current.submit();
          // console.log("pristing", formRef?.current)
          setFirstSubmit(true);
          if (!formStepsValidity[UnderwritingSteps.company]) {
            scrollToFirstError();
            resolve({
              success: false,
              values: formRef?.current?.getState().values,
              pristine: formRef?.current?.getState().pristine,
              newDocIds: newUploadedDocIds,
              deleteDocIds: deleteDocIds,
            });
          } else {
            deleteFileKeysOnSubmit();
            resolve({
              success: true,
              values: formRef?.current?.getState().values,
              pristine: formRef?.current?.getState().pristine,
              newDocIds: newUploadedDocIds,
              deleteDocIds: deleteDocIds,
            });
          }
        });
      },
    }),
    [formStepsValidity, newUploadedDocIds, deleteDocIds]
  );

  const deleteFileKeysOnSubmit = () => {
    if (deleteFileKeys && deleteFileKeys.length) {
      for (const fileKey of deleteFileKeys) {
        try {
          API.post("/app/DeleteDoc", { key: fileKey }).then((res) => res.data);
        } catch (error) {
          console.log("error", error);
        }
      }
    }
  };

  // unstable_usePrompt({
  //     message: "Are you sure?",
  //     when: ({ currentLocation, nextLocation }) =>
  //         !formPristine &&
  //         currentLocation.pathname !== nextLocation.pathname,
  // } as any);

  const handleAddFounderScroll = useCallback(
    (divId) => {
      const container = sectionRootContainerRef.current;
      const element = document.getElementById(divId);
      console.log("founderElement", element);
      console.log("container");
      if (container) {
        console.log("containerRef", container);
        container?.scrollTo({
          top: element.offsetTop,
          behavior: "smooth",
        });
      }
    },
    [sectionRootContainerRef]
  );

  const scrollToFirstError = () => {
    try {
      const formState = formRef.current.getState();
      const { errors } = formState;
      // console.log('errors', errors);
      // console.log('form state', formState);
      // console.log('formState', formState)
      const firstErrorField = findFirstErrorField(errors);
      // console.log('firstErrorField', firstErrorField);
      setTimeout(() => {
        let errorField =
          document.getElementById(firstErrorField) ??
          document.querySelector(`[name="${firstErrorField}"]`);
        if (!errorField && firstErrorField?.includes("[")) {
          // "gstStatements[0].gstin" -> "gstStatements[0]".
          errorField = document.getElementById(firstErrorField.split(".")[0]);
        }
        if (errorField) {
          errorField.scrollIntoView({ behavior: "smooth" });
        }
      }, 0);
    } catch (error) {
      console.log("error", error);
    }
  };

  const companyFileValidate = (values) => {
    let errors = {};
    const companyDoc = values.companyDoc;
    Object.keys(companyDoc).forEach((key) => {
      const docField = companyDoc[key];
      if (!docField) {
        errors[key] = "Upload Document";
      } else {
        const error = validateFile(docField?.selectedFile);
        errors[key] = error;
      }
    });
    return errors;
  };

  const companyDocObj = useMemo(() => {
    return companyRows.reduce((prev, curVal) => {
      prev[curVal.key] = curVal;
      return prev;
    }, {});
  }, [companyRows]);

  const [companyDocRows, setCompanyDocRows] = useState(companyRows);

  // const { input: { value: currentAddressSameAsRegisteredValue } } = useField(`currentSameAsRegistered`, { subscription: { value: true } });
  const currentAddressSameAsRegisteredValue = getIn(
    formRef?.current?.getState().values,
    "currentSameAsRegistered"
  );

  // console.log('currentAddressSameAsRegisteredValue', currentAddressSameAsRegisteredValue)
  useEffect(() => {
    if (currentAddressSameAsRegisteredValue == "no") {
      // console.log('coming inside')
      const utilityRow = {
        title: "Address Proof",
        subTitle: "Mentioned address should match your operating address.",
        key: companyDocsKeys.addressProof,
        isMultiFile: false,
      };
      setCompanyDocRows((prevRows) => {
        const removedUtitlityRow = prevRows.filter(
          (row) => row.key !== companyDocsKeys.addressProof
        );
        // console.log('removedUtitlityRow', removedUtitlityRow)
        const updatedRows = [...removedUtitlityRow, utilityRow];
        return updatedRows;
      });
    } else {
      // console.log('coming inside else')
      setCompanyDocRows((prevRows) => {
        const updatedRows = prevRows.filter(
          (row) => row.key !== companyDocsKeys.addressProof
        );
        return updatedRows;
      });
    }
  }, [currentAddressSameAsRegisteredValue]);

  return (
    <div className={styles.rootContainer} ref={sectionRootContainerRef}>
      <Form
        initialValues={formData}
        keepDirtyOnReinitialize
        mutators={{
          ...arrayMutators,
          setFieldTouched: setFieldTouchedMutator,
        }}
        onSubmit={(values) => {
          return {};
        }}
        validate={(values) => {
          let errors = {};
          const { errors: companFieldErrors } = validateCompanyFields(values);
          const { errors: companyDocErrors } = validateCompanyDocs(
            values,
            underwritingFileJobs
          );

          const { errors: gstErrors } = validateGstDetails(
            values,
            underwritingFileJobs,
            gstApiErrors,
            gstAnalysisJobs,
            ApplicationContextType.underwriting
          );

          errors = {
            ...errors,
            ...companFieldErrors,
            // ...companyDocErrors, all the document upload feilds are removed.
            ...gstErrors,
          };

          // const

          // console.log('gstErrors', gstErrors)
          console.log("company errors", errors);
          console.log("gstStatements", values?.gstStatements);
          return errors;
        }}
        render={({ values: formValues, handleSubmit, form }) => {
          // formRefs.current[UnderwritingSteps.company] = form;
          formRef.current = form;
          return (
            <form className={styles.formWrapper} onSubmit={handleSubmit}>
              <FormSpy
                subscription={{ valid: true, pristine: true, values: true }}
                onChange={(state) => {
                  // console.log('formState', state)
                  // formStepsValidityRef.current[UnderwritingSteps.company] = !state.valid;
                  setFormPristine(state.pristine);
                  setStepValidity(UnderwritingSteps.company, state.valid);
                  saveToLocalStorage?.(state.values);
                }}
              />
              <div className={styles.formRootContainer}>
                <div
                  ref={sectionRefs.current.companyDetails as any}
                  className={styles.sectionContainer}
                >
                  <div className={styles.sectionTitle}>Company Details</div>
                  <div className={styles.sectionFieldsContainer}>
                    <Field
                      name="companyName"
                      validate={(value) => {
                        if (!value) {
                          return `Enter your company's name`;
                        }
                      }}
                    >
                      {(props) => (
                        <OutlinedFormInput
                          {...props.input}
                          fieldType="lg"
                          label="Company Name"
                          placeholder="ABC Pvt Ltd"
                          name={props.input.name}
                          value={props.input.value}
                          onChange={props.input.onChange}
                          error={props.meta.error}
                          touched={props.meta.touched}
                          maxLength={100}
                        />
                      )}
                    </Field>
                    <Field
                      name="brandName"
                      validate={(value) => {
                        if (!value) {
                          return `Enter your brand's name`;
                        }
                      }}
                    >
                      {(props) => (
                        <OutlinedFormInput
                          {...props.input}
                          label="Brand Name"
                          placeholder="ABC Fashion"
                          fieldType="lg"
                          name={props.input.name}
                          value={props.input.value}
                          onChange={props.input.onChange}
                          error={props.meta.error}
                          touched={props.meta.touched}
                          maxLength={100}
                        />
                      )}
                    </Field>
                    <Field
                      name="brandCategory"
                      validate={(value) => {
                        if (!value) {
                          return `Select your brand's product category`;
                        }
                      }}
                    >
                      {(props) => (
                        <MeritoSelectField
                          noOptionsMessage={
                            "No category matches your search criteria"
                          }
                          placeholder="Select Category"
                          error={props.meta.error}
                          touched={props.meta.touched}
                          isClearable={false}
                          options={Object.values(brandCategoriesObj)}
                          label="Brand Category"
                          optionLabelKey="category"
                          value={props.input.value}
                          onChange={props.input.onChange}
                          {...props.input}
                        />
                      )}
                    </Field>
                    <Field
                      name="website"
                      validate={(value) => {
                        const errorType = companyWebsiteValidation(value);
                        if (errorType == validationErrorTypes.required) {
                          return `Enter your store’s website`;
                        } else if (errorType == validationErrorTypes.invalid) {
                          return `Invalid website. Enter a valid URL.`;
                        }
                      }}
                    >
                      {(props) => (
                        <OutlinedFormInput
                          {...props.input}
                          placeholder="example.com"
                          label="Website"
                          fieldType="lg"
                          name={props.input.name}
                          value={props.input.value}
                          onChange={props.input.onChange}
                          error={props.meta.error}
                          touched={props.meta.touched}
                          maxLength={250}
                        />
                      )}
                    </Field>
                    {/* <Field
                                            name='companyPan'
                                            validate={(value) => {
                                                const errorType = panNumbervalidation(value);
                                                if (errorType == validationErrorTypes.required) {
                                                    return `Enter your company's PAN card number`
                                                } else if (errorType == validationErrorTypes.invalid) {
                                                    return `Enter a valid PAN card number`
                                                }
                                            }}
                                        >
                                            {(props) => (
                                                <OutlinedFormInput
                                                    {...props.input}
                                                    label="Company's PAN"
                                                    placeholder='AAAAA9999A'
                                                    fieldType='lg'
                                                    name={props.input.name}
                                                    value={props.input.value}
                                                    onChange={props.input.onChange}
                                                    error={props.meta.error}
                                                    touched={props.meta.touched}
                                                    maxLength={10}
                                                    allUpercase={true}
                                                />
                                            )}
                                        </Field> */}
                    {/* <Field
                      name='gstNumber'
                      validate={(value) => {
                        const errorType = gstNumberValidation(value);
                        if (errorType == validationErrorTypes.required) {
                          return `Enter your company’s GST number`
                        } else if (errorType == validationErrorTypes.invalid) {
                          return `Enter a valid company GST number`
                        }
                      }}
                    >
                      {(props) => (
                        <OutlinedFormInput
                          {...props.input}
                          label="GST Number"
                          fieldType='lg'
                          placeholder='22AAAAA9999A1Z5'
                          name={props.input.name}
                          value={props.input.value}
                          onChange={props.input.onChange}
                          error={props.meta.error}
                          touched={props.meta.touched}
                          maxLength={15}
                          allUpercase={true}
                        />
                      )}
                    </Field> */}
                    {/* <Field
                      name="cinNumber"
                      validate={(value) => {
                        const errorType = cinNumberValidation(value);
                        if (errorType == validationErrorTypes.required) {
                          return `Enter your company’s CIN`;
                        } else if (errorType == validationErrorTypes.invalid) {
                          return `Enter a valid company CIN`;
                        }
                      }}
                    >
                      {(props) => (
                        <OutlinedFormInput
                          {...props.input}
                          label="Corporate Identification Number"
                          fieldType="lg"
                          placeholder="L12345MH2023PLC012345"
                          name={props.input.name}
                          value={props.input.value}
                          onChange={props.input.onChange}
                          error={props.meta.error}
                          touched={props.meta.touched}
                          maxLength={21}
                          allUpercase={true}
                        />
                      )}
                    </Field>

                    <Field
                      name="cinCompanyName"
                      validate={(value) => {
                        if (!value) {
                          return `Enter your CIN company name`;
                        }
                      }}
                    >
                      {(props) => (
                        <OutlinedFormInput
                          {...props.input}
                          label="CIN Company Name"
                          fieldType="lg"
                          placeholder="Acme Pvt Ltd"
                          name={props.input.name}
                          value={props.input.value}
                          onChange={props.input.onChange}
                          error={props.meta.error}
                          touched={props.meta.touched}
                          maxLength={250}
                          allUpercase={false}
                        />
                      )}
                    </Field>

                    <Field
                      validate={(values) => {
                        // console.log('values', values);
                        if (!values) {
                          return `Enter your company’s date of incorporation`;
                        } else if (
                          !(
                            dayjs(values, "DD/MM/YYYY").isValid() ||
                            dayjs(values).isValid()
                          )
                        ) {
                          return `Enter a valid date`;
                        } else if (dayjs(values).isAfter(dayjs())) {
                          return `Enter a date from the past or today`;
                        }
                      }}
                      name={`dateOfIncorporation`}
                    >
                      {(props) => (
                        <MeritoDateField
                          {...props.input}
                          label="Date of Incorporation"
                          name={props.input.name}
                          onChange={props.input.onChange}
                          error={props.meta.error}
                          value={props.input.value}
                          touched={props.meta.touched}
                          format={"DD/MM/YYYY"}
                          showIcon={false}
                          size="md2"
                        />
                      )}
                    </Field>

                    <Field
                      name="registeredAddress"
                      validate={(value) => {
                        if (!value) {
                          return `Enter address`;
                        }
                      }}
                    >
                      {(props) => (
                        <OutlinedFormInput
                          {...props.input}
                          label="Registered Company Address"
                          fieldType="lg"
                          placeholder="102, Acme Building, BKC, Mumbai, 400060."
                          name={props.input.name}
                          value={props.input.value}
                          onChange={props.input.onChange}
                          error={props.meta.error}
                          touched={props.meta.touched}
                          // maxLength={250}
                          // allUpercase={false}
                        />
                      )}
                    </Field>

                    <Field
                      validate={(values) => {
                        if (!values) {
                          return `Please select an option`;
                        }
                      }}
                      name="currentSameAsRegistered"
                    >
                      {(props) => {
                        return (
                          <MeritoRadioButton
                            size="md2"
                            radioLabels={[
                              {
                                label: "Yes",
                                value: "yes",
                              },
                              {
                                label: "No",
                                value: "no",
                              },
                            ]}
                            isLabelBold={true}
                            name={props.input.name}
                            onChange={props.input.onChange}
                            error={props.meta.error}
                            touched={props.meta.touched}
                            value={props.input.value}
                            label={
                              "Is the registered address the same as the current address?"
                            }
                            {...props.input}
                          />
                        );
                      }}
                    </Field> */}
                    {/* <OnChange name='currentSameAsRegistered'>
                                            {(values, previousValue) => {
                                                if (values == "no") {
                                                    setShowUtilityRow(true)
                                                }
                                                if (values == "yes") {
                                                    setShowUtilityRow(false)
                                                }
                                            }}
                                        </OnChange> */}

                    {/*<div className={styles.companyDocRootContainer}>
                      <div className={styles.label}>Upload Documents</div>
                      <TableForm
                        footerText={
                          <>
                            Supported file formats: JPG, PNG and PDF. Max file
                            size: 20MB.
                          </>
                        }
                        headerColumns={companyDocColumns}
                      >
                        {companyDocRows.map((item, index) => {
                          const isLast = index == companyDocRows.length - 1;
                          return (
                            <TableUploadRow
                              key={item.key}
                              stepName={UnderwritingSteps.company}
                              documentType="companyDocuments"
                              fieldItem={item}
                              isLast={isLast}
                              fieldName={`companyDoc.${item.key}`}
                              formCompleteStatus={formCompleteStatus}
                              newUploadedDocIds={newUploadedDocIds}
                              setNewUploadedDocIds={setNewUploadedDocIds}
                              deleteDocIds={deleteDocIds}
                              setDeleteDocIds={setDeleteDocIds}
                            />
                          );
                        })}
                        {/* <FieldArray name='companyDoc'>
                                                    {({ fields }) => {
                                                        return (
                                                            <>
                                                                {fields.map((name, index) => {
                                                                    const isLast = index == fields.length - 1;
                                                                    const fieldKey = getIn(formValues, name);
                                                                    return (
                                                                        <TableUploadRow
                                                                            stepName={UnderwritingSteps.company}
                                                                            documentType='companyDocuments'
                                                                            fieldItem={companyDocObj[fieldKey?.key]}
                                                                            fieldName={name}
                                                                            isLast={isLast}
                                                                            formCompleteStatus={formCompleteStatus}
                                                                            newUploadedDocIds={newUploadedDocIds}
                                                                            setNewUploadedDocIds={setNewUploadedDocIds}
                                                                            deleteDocIds={deleteDocIds}
                                                                            setDeleteDocIds={setDeleteDocIds} />
                                                                    )
                                                                })}
                                                            </>
                                                        )
                                                    }}
                                                </FieldArray> */}
                    {/*</TableForm>
                    </div>  */}
                  </div>
                </div>

                <div
                  ref={sectionRefs.current.gstDetails as any}
                  className={styles.sectionContainer}
                >
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>GST Accounts</div>
                    <div className={styles.sectionSubTitle}>
                      Add all GST accounts used by your business.
                    </div>
                  </div>
                  <GstDetails
                    formCompleteStatus={formCompleteStatus}
                    newUploadedDocIds={newUploadedDocIds}
                    setNewUploadedDocIds={setNewUploadedDocIds}
                    deleteDocIds={deleteDocIds}
                    setDeleteDocIds={setDeleteDocIds}
                    gstApiErrors={gstApiErrors}
                    setGstApiErrors={setGstApiErrors}
                    formContext={UnderwritingFormContext}
                    formType={ApplicationContextType.underwriting}
                  />
                </div>

                {/* <div
                  ref={sectionRefs.current.shareholderDetails as any}
                  className={styles.sectionContainer}
                >
                  <div className={styles.sectionTitle}>
                    Shareholding Pattern
                  </div>
                  <ShareholderDetails />
                </div> */}
              </div>
            </form>
          );
        }}
      />

      <div className={styles.scrollBtnContainer}>
        {showScrollButton && (
          <MeritoButton
            size="md2"
            onClick={scrollToNextSection}
            isIconToRight={true}
            btnText="Scroll to Next Section"
            type="secondary-1"
            icon={<DownArrowIcon />}
          />
        )}
      </div>
    </div>
  );
});

export default CompanyDetails;
